import { Component, OnInit } from '@angular/core';
import { AlertService, AuthenticationService } from './_services';
import { User } from './_models';
import {NavigationExtras, NavigationStart, Router} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { AESEncryptDecryptService } from "./_services/aesencrypt-decrypt.service";
import { Constants } from './constants/constants';
import {NotifierService} from 'angular-notifier';
import {UtilsService} from './_services/utils.service';
import {PunchOutboxService} from './_services/punch-outbox.service';
import {NATIVE_BOOT_CODE_KEY} from './_services/device-mode-boot.service';
import {NATIVE_BOOT_SESSION_KEY} from './_services/device-mode-boot.service';
import {Capacitor} from '@capacitor/core';
import {App} from '@capacitor/app';
import {resolveLandingPath} from './_helpers/role-landing';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html'
})

export class AppComponent implements OnInit {
  navbar = false;
  currentUser: User;
  showMenu: boolean;
  loginPage: boolean;
  registerPage: boolean;
  apiUrl: string = environment.apiUrl;
  currentRole: string;

  constructor(private authService: AuthenticationService, private utilsService: UtilsService, private alertService: AlertService,
              private notifier: NotifierService, private _AESEncryptDecryptService: AESEncryptDecryptService, private router: Router,
              private translate: TranslateService, private punchOutbox: PunchOutboxService) {
    this.currentUser = utilsService.getCurrentUser();
    if (this.currentUser) {
      this.currentRole = this.currentUser?.selectedRole;

      translate.setDefaultLang('English');
      router.events.forEach((event) => {
        if (event instanceof NavigationStart) {
          // console.log('each event url :' + event.url);
          if (!this.currentUser) {
            this.currentUser = utilsService.getCurrentUser();
          }
          this.showMenu = (this.currentUser ? this.currentRole !== Constants.KIOSK : false);
          this.loginPage = event.url.includes('/login');
          this.registerPage = event.url.includes('/register');
        }
      });
      this.authService.getLoggedInSubject().subscribe(response => {
        this.navbar = response;
      },
        error => {
          this.alertService.error(error);
        });
    } else {
      let navigationExtras: NavigationExtras = {
        queryParamsHandling: 'preserve',
        preserveFragment: true
      };
      // '/hub-login' must be excluded: the Driven Hub secret-login handoff always arrives
      // with no stored user, and this bootstrap redirect would otherwise send it home
      // before HubLoginComponent can redeem the handoff. Note '/hub-login' does NOT match
      // the '/login' check below (the character before 'login' is '-', not '/').
      if (!window.location.href.includes('/login') && !window.location.href.includes('/hub-login') && !window.location.href.includes('/login-code') && !window.location.href.includes('/cloverAuth') && !window.location.href.includes('/receipt') && !window.location.href.includes('/register') && !window.location.href.includes('customer/signup') && !window.location.href.includes('/youtube') && !window.location.href.includes('/order') && !window.location.href.includes('/pay') && !window.location.href.includes('/website')) {
        this.router.navigate([''], {queryParamsHandling: 'preserve'});
      }

    }
  }

  ngOnInit() {
    if (localStorage.getItem('isDeviceMode') === 'true') {
      const meta = document.querySelector('meta[name="viewport"]');
      if (meta) {
        meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0, shrink-to-fit=no, viewport-fit=cover');
      }
    }

    // console.log('app.component.ts OnInit');
    const hostname = window.location.hostname;
    const path = window.location.pathname;
    if ((hostname.includes('table') || hostname.includes('kiosk')) && (path === '/' || path === '/index.html')) {
      this.router.navigate(['/login-code']);
    }

    // Capacitor native (DineNGo Android Runner): if the boot initializer staged
    // a provisioned activation code because there was no existing session,
    // navigate to /login-code for silent auto-activation. replaceUrl so the
    // back stack doesn't strand the user on the empty home route.
    if (Capacitor.isNativePlatform()) {
      this.installNativeDeepLinkHandler();
      const bootCode = localStorage.getItem(NATIVE_BOOT_CODE_KEY);
      if (bootCode) {
        localStorage.removeItem(NATIVE_BOOT_CODE_KEY);
        this.router.navigate(['/login-code'], {
          queryParams: { isDevice: '1', code: bootCode },
          replaceUrl: true,
        });
      } else if (localStorage.getItem(NATIVE_BOOT_SESSION_KEY) === 'true') {
        localStorage.removeItem(NATIVE_BOOT_SESSION_KEY);
        const user = this.utilsService.getCurrentUser();
        const role = user?.selectedRole || user?.role?.[0];
        // Native register appliances mirror the successful login flow: staff
        // roles land on the full-screen cashier rather than an admin dashboard.
        // Non-register roles retain their normal role landing behavior.
        const registerRole = role === Constants.ADMIN
          || role === Constants.MANAGER
          || role === Constants.SERVER;
        const landing = registerRole
          ? '/cashier'
          : resolveLandingPath(role, user?.preferences?.defaultLandingPath);
        this.router.navigateByUrl(landing, { replaceUrl: true });
      } else if (!this.utilsService.getCurrentUser()) {
        this.router.navigate(['/login'], {
          queryParams: { isMobile: 'true', mobile: 'true', isDevice: 'true' },
          replaceUrl: true,
        });
      }
    }
    // Best-effort: drain any punches that were queued on a prior offline
    // session. Service constructor already wired the `online` listener for
    // the runtime case; this catches the boot path. Failure is silent —
    // entries stay in the queue for the next attempt.
    if (this.punchOutbox) {
      this.punchOutbox.flush().catch(() => { /* ignore */ });
    }
  }

  private installNativeDeepLinkHandler(): void {
    App.addListener('appUrlOpen', ({ url }) => {
      try {
        const incoming = new URL(url);
        let path = incoming.pathname || '/';
        if (incoming.protocol === 'dinengo:' && incoming.host === 'oauth' && path === '/cloverAuth') {
          path = '/cloverAuth/cloverAuth';
        }
        const query = incoming.search || '';
        this.router.navigateByUrl(`${path}${query}`, { replaceUrl: true });
      } catch {
        // Ignore malformed external intents; never expose an arbitrary URL in
        // the privileged register WebView.
      }
    });
  }
}
